import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import appVersionClient from '../../../../../../clients/app-version';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    minWidth: 300,
  },
  qrContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(3),
    gap: theme.spacing(2),
  },
  qrCode: {
    padding: theme.spacing(2),
    backgroundColor: 'white',
    borderRadius: theme.shape.borderRadius,
  },
  downloadButton: {
    marginTop: theme.spacing(1),
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
}));

const FALLBACK_SCHEME = process.env.REACT_APP_DEEP_LINK_SCHEME || 'regionas';

const QRCodeGenerator = ({ objectId, goldCoinAmount, disabled }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [appSchema, setAppSchema] = useState(null);
  const qrRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    appVersionClient
      .getAppVersion()
      .then((res) => {
        if (!cancelled && res?.schema) setAppSchema(res.schema);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  const scheme = appSchema || FALLBACK_SCHEME;

  const getCollectDeepLink = () => {
    if (!objectId) return '';
    return `${scheme}://collect/${objectId}`;
  };

  const handleOpen = () => {
    if (!disabled && objectId) {
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDownload = () => {
    if (!qrRef.current) return;

    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;

    // Convert SVG to canvas, then to PNG
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    // Set canvas size with padding
    const padding = 20;
    const size = 256 + padding * 2;
    canvas.width = size;
    canvas.height = size;

    img.onload = () => {
      try {
        // Fill white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, size, size);
        
        // Draw QR code
        ctx.drawImage(img, padding, padding, 256, 256);
        
        // Convert to blob and download
        canvas.toBlob((blob) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `qr-code-object-${objectId}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
          }
        }, 'image/png');
      } catch (error) {
        console.error('Error generating PNG:', error);
      }
    };

    img.onerror = () => {
      console.error('Error loading SVG image');
    };

    const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    img.src = url;
    
    // Clean up the blob URL after a delay
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const deepLink = getCollectDeepLink();

  if (!objectId) {
    return null;
  }

  return (
    <>
      <Button
        variant="outlined"
        color="primary"
        onClick={handleOpen}
        disabled={disabled || !objectId}
        fullWidth
      >
        Generuoti aukso monetų gavimo QR kodą
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>
          Aukso monetų gavimo QR kodas
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={handleClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className={classes.qrContainer} ref={qrRef}>
            <Paper className={classes.qrCode} elevation={2}>
              <QRCodeSVG
                value={deepLink}
                size={256}
                level="H"
                includeMargin={true}
              />
            </Paper>
            <Typography variant="body2" color="textSecondary" align="center">
              {deepLink}
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDownload}
            variant="contained"
            color="primary"
            startIcon={<GetAppIcon />}
            className={classes.downloadButton}
          >
            Atsisiųsti PNG
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

QRCodeGenerator.propTypes = {
  objectId: PropTypes.string,
  goldCoinAmount: PropTypes.number,
  disabled: PropTypes.bool,
};

QRCodeGenerator.defaultProps = {
  objectId: null,
  goldCoinAmount: null,
  disabled: false,
};

export default QRCodeGenerator;
